import Add16 from '@carbon/icons-react/es/add/16';
import { isNil, omitBy } from 'lodash';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { IntegrationProvider } from 'api/integrations';
import {
  JiraApi,
  JiraConfig,
  JiraIssueType,
  MappingMechanism,
  ProjectMappingMechanism,
} from 'api/integrations/jira';
import { ProjectId, TaskId } from 'api/models';
import { Button } from 'components/atoms/button';
import { OzoneDrawer } from 'components/atoms/drawer/ozone-drawer';
import { Form } from 'components/atoms/form';
import { getJiraFormValues } from 'components/molecules/integrations-commons';
import { toastService } from 'components/molecules/toast';
import { Task } from 'data/tasks/index';
import { useTranslation } from 'hooks/use-translation';
import {
  GET_JIRA_ISSUE,
  useQueryGetJiraFields,
  useQueryGetJiraProjects,
  useQueryJiraEpic,
  useQueryJiraIssueTypes,
} from 'queries/jira-integration';
import { useProjectDetailQuery } from 'queries/project';
import { getTaskDetailQueryKey } from 'queries/task';
import { logger } from 'services';
import { JiraForm } from './jira-issue-form';

interface JiraIssueDrawer {
  taskData: Task;
  jiraConfig: JiraConfig;
  showDrawer: boolean;
  onClose: () => void;
}

export const JiraIssueDrawer: FC<JiraIssueDrawer> = ({
  taskData,
  jiraConfig,
  showDrawer = false,
  onClose,
}) => {
  const {
    integrationId,
    settingsMeta: {
      fields,
      fixedProjectId,
      fixedEpicId: jiraFixedEpicId,
      defaultIssueTypeId = '',
      epicMappingMechanism,
    },
  } = jiraConfig;

  /**
   * #1 Load RL project details
   */
  const { data: projectDetails } = useProjectDetailQuery(
    ProjectId(taskData.project),
  );

  const [form] = Form.useForm();
  const [creatingIssue, setCreatingIssue] = useState(false);

  const [jiraProjectId, setJiraProjectId] = useState<string | null>(
    fixedProjectId ?? null,
  );
  const [jiraIssueTypeId, setJiraIssueTypeId] = useState<string | null>(
    defaultIssueTypeId ?? null,
  );

  const [jiraEpicId, setJiraEpicId] = useState<string | null>(
    jiraFixedEpicId ?? null,
  );

  const queryClient = useQueryClient();
  const cancelText = useTranslation('cancel');
  const submitText = useTranslation('create');

  const isFixedProjectMapping = useMemo(
    () =>
      jiraConfig.settingsMeta.projectMappingMechanism ===
      ProjectMappingMechanism.Fixed,
    [jiraConfig],
  );

  useEffect(() => {
    const derivedEpicId =
      (projectDetails?.linkedResources ?? []).find(
        ({ externalIntegrationType }) =>
          externalIntegrationType === IntegrationProvider.Jira,
      )?.externalResourceId ??
      (epicMappingMechanism === MappingMechanism.Fixed
        ? jiraFixedEpicId ?? null
        : null);

    setJiraEpicId(derivedEpicId);
  }, [projectDetails, jiraFixedEpicId, epicMappingMechanism]);

  /**
   * #2 Load Jira Epics list and set epic field value on jira-form
   */
  const { data: epicsList, isLoading: loadingEpics } = useQueryJiraEpic(
    jiraConfig.integrationId,
    jiraProjectId!,
    {
      enabled: Boolean(
        showDrawer &&
          jiraConfig.integrationId &&
          jiraProjectId != null &&
          projectDetails !== undefined,
      ),
      onSuccess: (epics) => {
        const selectedEpic = epics.find(({ id }) => id === jiraEpicId);

        if (selectedEpic && isFixedProjectMapping) {
          form.setFieldsValue({
            epicId: {
              value: selectedEpic.id,
              valueStr: selectedEpic.id,
              label: selectedEpic.name,
            },
          });
        }

        return true;
      },
    },
  );
  const jiraIntegration = projectDetails?.linkedResources?.find(
    (e) => e.externalIntegrationType === 'jira',
  );
  const epicsListForThisProject = epicsList?.filter(
    (e) => e.id?.toString() === jiraIntegration?.externalResourceId?.toString(),
  );

  /**
   * #3 Load jira issue type options, to be used to select and issue type
   * and also to set issuetype field value on jira-form
   */
  const { data: issueTypes, isLoading: loadingIssueTypes } =
    useQueryJiraIssueTypes(jiraConfig.integrationId, jiraProjectId!, {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled:
        showDrawer && !!jiraConfig.integrationId && jiraProjectId != null,
      onSuccess: (res: JiraIssueType[]) => {
        const selectedIssueType = (res ?? []).find(
          ({ id }) => id === jiraIssueTypeId,
        );

        if (selectedIssueType && isFixedProjectMapping) {
          form.setFieldsValue({
            issuetype: {
              value: selectedIssueType.id,
              valueStr: selectedIssueType.id,
              label: selectedIssueType.name,
            },
          });
        }
      },
    });

  const selectedIssueTypeMeta = useMemo(() => {
    const selected = issueTypes?.find(
      (issueType) => issueType.id === jiraIssueTypeId,
    );

    return {
      label: selected?.name,
      value: selected?.id,
      valueStr: selected?.id,
    };
  }, [jiraIssueTypeId, issueTypes]);
  /**
   * #4 Get fields from jira as the field options are dynamic and need to fetch everytime.
   * so to show options/default value i need to make a fields call
   */
  const { data: fieldsList, isFetching: loadingFields } = useQueryGetJiraFields(
    integrationId,
    jiraProjectId!,
    jiraIssueTypeId!,
    {
      enabled: showDrawer && jiraProjectId != null && jiraIssueTypeId != null,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  /**
   * ==== Jira project realted =====
   */
  const { data: jiraProjects, isLoading: loadingJiraProjects } =
    useQueryGetJiraProjects(jiraConfig.integrationId, {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: Boolean(
        showDrawer &&
          jiraConfig.integrationId &&
          jiraConfig.settingsMeta.projectMappingMechanism ===
            ProjectMappingMechanism.PromptDuringIssueCreation,
      ),
    });

  const selectedProjectMeta = useMemo(() => {
    const selected = jiraProjects?.find(
      (jiraProject) => jiraProject.id === jiraProjectId,
    );

    return { label: selected?.name, value: selected?.id };
  }, [jiraProjectId, jiraProjects]);

  /**
   * TODO: use mutation and refactor
   */
  const onIssueCreation = () => {
    setCreatingIssue(true);
    const payload = form.getFieldsValue(true);

    form
      .validateFields()
      .then(() => {
        JiraApi.createJiraIssue(jiraConfig?.integrationId, {
          rlTaskId: taskData.taskId,
          // removes empty/ null/ undefined values from payload.
          ...omitBy(payload, (value) => isNil(value) || value === ''),
          summary: payload?.taskName,
          issuetype: payload?.issuetype?.value,
          epicId: payload?.epicId?.value,
          projectId: payload?.projectId?.value,
        })
          .then(() => {
            toastService({
              toastContainer: {},
              title: 'Issue Created',
              message: 'Jira issue created succesfully',
            }).success();
            queryClient.invalidateQueries(
              getTaskDetailQueryKey(TaskId(taskData.project, taskData.taskId)),
            );
            queryClient.invalidateQueries(GET_JIRA_ISSUE);
            onClose();
          })
          .catch(() => {
            setCreatingIssue(false);
            toastService({
              toastContainer: {},
              title: 'Error',
              message: 'Unable to create jira issue',
            }).error();
          });
      })
      .catch((error) => {
        setCreatingIssue(false);
        logger.warn('Jira creation failed', { error });
      });
  };

  const handleOnClose = () => {
    setCreatingIssue(false);
    onClose();
  };

  const handleProjectChange = async (projectId: string) => {
    form.resetFields(['issuetype', 'epicId']);

    await setJiraProjectId(projectId);
    await setJiraIssueTypeId(null);
    await setJiraEpicId(null);
  };

  const handleIssueTypeChange = async (issueTypeId: string) => {
    await setJiraIssueTypeId(issueTypeId);
  };

  const handleEpicChange = async (epicId: string) => {
    await setJiraEpicId(epicId);
  };

  const formSetFieldsValueRef = useRef(form.setFieldsValue);

  useEffect(() => {
    formSetFieldsValueRef.current = form.setFieldsValue;
  }, [formSetFieldsValueRef, form]);

  useEffect(() => {
    let formData = {
      taskName: taskData?.taskName,
      description: taskData?.taskDescription ?? '',
      projectId: selectedProjectMeta,
      issuetype: selectedIssueTypeMeta,
    };

    if (isFixedProjectMapping) {
      formData = { ...formData, ...getJiraFormValues(fields) };
    }

    formSetFieldsValueRef.current?.(formData);
  }, [
    formSetFieldsValueRef,
    fields,
    selectedProjectMeta,
    selectedIssueTypeMeta,
    isFixedProjectMapping,
    taskData,
  ]);

  return (
    <OzoneDrawer
      visible={showDrawer}
      title={<FormattedMessage id="task.new_jira_issue" />}
      subTitle={
        <FormattedMessage
          id="task.new_jira_issue.from"
          values={{ taskName: taskData.taskName }}
        />
      }
      onClose={handleOnClose}
      width={680}
      footer={
        <div
          style={{
            textAlign: 'right',
          }}
        >
          <Button style={{ marginRight: 8 }} onClick={handleOnClose}>
            {cancelText}
          </Button>
          <Button
            type="primary"
            icon={<Add16 />}
            iconPlacement="right"
            loading={creatingIssue}
            onClick={onIssueCreation}
            disabled={epicsList === undefined}
          >
            {submitText}
          </Button>
        </div>
      }
    >
      <JiraForm
        jiraConfig={jiraConfig}
        form={form}
        isLoading={loadingEpics || loadingIssueTypes || loadingFields}
        isFixedProjectMapping={isFixedProjectMapping}
        fieldsList={fieldsList}
        loadingFieldsList={loadingFields}
        issueTypes={issueTypes}
        loadingIssueTypes={loadingIssueTypes}
        epicsList={epicsList}
        epicsListForThisProject={epicsListForThisProject}
        loadingEpicsList={loadingEpics}
        jiraProjects={jiraProjects}
        loadingJiraProjects={loadingJiraProjects}
        onProjectChange={handleProjectChange}
        onIssueTypeChange={handleIssueTypeChange}
        onEpicChange={handleEpicChange}
        disableEpicSelection={Boolean(jiraEpicId)}
      />
    </OzoneDrawer>
  );
};
